import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { Logo } from "components/logo"
import { PageLoader } from "components/page-loader"
import { SignUpForm } from "components/signup-form"
import { useAuth } from "hooks/use-auth"
import { useEffect } from "react"
import { z } from "zod"
import { trpc } from "@/lib/trpc"
import { getRegionDetails } from "@/server/region"

const signUpSearchSchema = z.object({
  redirect: z.string().optional(),
})

export const Route = createFileRoute("/_public/sign-up/")({
  loader: async () => {
    const regionDetails = await getRegionDetails()
    return { regionDetails }
  },
  validateSearch: signUpSearchSchema,
  component: RouteComponent,
})

function RouteComponent() {
  const { isAuthenticated, isLoading } = useAuth()
  const navigate = useNavigate()
  const search = Route.useSearch()
  const { regionDetails } = Route.useLoaderData()

  const { data: user, isLoading: meLoading } = trpc.users.me.useQuery(undefined, {
    enabled: isAuthenticated,
  })

  useEffect(() => {
    if (!isLoading && !meLoading && isAuthenticated) {
      const isProfessional = user?.userType === "professional"
      const requestedRedirect = search.redirect

      let redirectTo: string

      if (isProfessional) {
        redirectTo = "/sign-up/pro"
      } else if (requestedRedirect && requestedRedirect !== "/sign-up/pro") {
        redirectTo = requestedRedirect
      } else {
        redirectTo = "/personal/dashboard"
      }
      const shouldClearRedirect = redirectTo === "/sign-up/pro"
      navigate({
        to: redirectTo,
        search: shouldClearRedirect ? { redirect: undefined } : undefined,
      })
    }
  }, [isAuthenticated, isLoading, meLoading, user?.userType, navigate, search.redirect])

  if (isLoading || (isAuthenticated && meLoading)) {
    return <PageLoader />
  }

  if (isAuthenticated) {
    return null
  }

  return (
    <div className="grid min-h-svh lg:grid-cols-2">
      <div className="flex flex-col gap-8 p-6 md:p-10 items-center justify-center">
        <div className="">
          <Logo width="max-w-[300px] max-h-[300px]" />
        </div>
        <div className="w-full max-w-xs">
          <SignUpForm redirect={search.redirect} regionDetails={regionDetails} />
        </div>
      </div>
      <div className="bg-sky-100 p-12 flex items-center justify-center">
        <img src="/images/login_illustration.svg" alt="Illustration" className="object-contain" />
      </div>
    </div>
  )
}
